import { Box, Button } from "@mui/material";
import {
  TextInput,
  SelectInput,
  required,
  useGetList,
  BooleanInput,
} from "react-admin";
import { useForm, FormProvider } from "react-hook-form";

import { MODULE_NAMES } from "@/constants";
import { FILTER_BASE_PROPS } from "@/constants/filters";
import { useTypedListContext } from "@/hooks";
import { BaseDateInput } from "@/inputs/BaseDateInput";
import { ReferenceAutoCompleteArrayInput } from "@/inputs/ReferenceAutoCompleteArrayInput";
import { ClientAppInput } from "@/modules/clientApps";
import { DeviceTypeInput } from "@/modules/deviceTypes/components";
import { PaymentProviderInput } from "@/modules/paymentProviderModel";
import { type TrackerRecord } from "@/modules/trackers/components/types";
import { TrafficSourceArrayInput } from "@/modules/trafficSources";

import { useStatisticsGroups } from "../api";

import {
  ALL_OPTION,
  STATISTICS_REBILLS_BY_CHOICES,
  STATISTICS_TIMEZONES_CHOICES,
  STATISTICS_TYPE_CHOICES,
} from "./constants";

const BOX_STYLE = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
};

export const StatisticsFiltersForm = () => {
  const { filterValues, setFilters } = useTypedListContext();
  const { data: statisticsGroups, isLoading: groupsLoading } =
    useStatisticsGroups();

  const { data: trackersData, isLoading: trackersLoading } =
    useGetList<TrackerRecord>(MODULE_NAMES.TRACKERS);

  const form = useForm({
    defaultValues: filterValues,
  });

  const onSubmit = (values: Record<string, unknown>) => {
    if (Object.keys(values).length > 0) {
      setFilters(values);
    }
  };

  return (
    <FormProvider {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)}>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: "0 16px", mb: 1 }}>
          <Box sx={BOX_STYLE}>
            <SelectInput
              source="group"
              label="Group"
              choices={statisticsGroups?.map((group) => ({
                id: group,
                name: group,
              }))}
              disabled={groupsLoading}
              validate={[required()]}
              fullWidth
              {...FILTER_BASE_PROPS}
            />
          </Box>
          <Box sx={BOX_STYLE}>
            <BaseDateInput
              key="dateStart"
              label="Date from"
              source="date_start"
              isRequired
              {...FILTER_BASE_PROPS}
            />
          </Box>
          <Box sx={BOX_STYLE}>
            <BaseDateInput
              key="dateEnd"
              label="Date to"
              source="date_end"
              isRequired
              {...FILTER_BASE_PROPS}
            />
          </Box>
          <Box sx={BOX_STYLE}>
            <SelectInput
              key="type"
              source="type"
              choices={STATISTICS_TYPE_CHOICES}
              validate={[required()]}
              {...FILTER_BASE_PROPS}
            />
          </Box>
          <Box sx={BOX_STYLE}>
            <ReferenceAutoCompleteArrayInput
              key="country"
              source="country"
              label="Countries"
              optionValue="code"
              module={MODULE_NAMES.COUNTRIES}
              disableCloseOnSelect
              limitTags={2}
              {...FILTER_BASE_PROPS}
            />
          </Box>
          <Box sx={BOX_STYLE}>
            <SelectInput
              key="tracker"
              source="tracker"
              optionValue="id"
              isLoading={trackersLoading}
              choices={[ALL_OPTION, ...(trackersData ?? [])]}
              validate={[required()]}
              {...FILTER_BASE_PROPS}
            />
          </Box>
          <Box sx={BOX_STYLE}>
            <TextInput
              key="tracker_campaign_id"
              source="tracker_campaign_id"
              label="Tracker Campaign ID"
              {...FILTER_BASE_PROPS}
            />
          </Box>
          <Box sx={BOX_STYLE}>
            <ClientAppInput
              key="clientApp"
              source="clientApp"
              {...FILTER_BASE_PROPS}
            />
          </Box>
          <Box sx={BOX_STYLE}>
            <DeviceTypeInput
              key="device_type_id"
              source="device_type_id"
              {...FILTER_BASE_PROPS}
            />
          </Box>
          <Box sx={BOX_STYLE}>
            <PaymentProviderInput
              key="payment_provider_id"
              source="payment_provider_id"
              {...FILTER_BASE_PROPS}
            />
          </Box>
          <Box sx={BOX_STYLE}>
            <TextInput
              key="tracker_zone_id"
              source="tracker_zone_id"
              label="Zone ID"
              {...FILTER_BASE_PROPS}
            />
          </Box>
          <Box sx={BOX_STYLE}>
            <TrafficSourceArrayInput
              key="our_traffic_source"
              source="our_traffic_source"
              limitTags={2}
              {...FILTER_BASE_PROPS}
            />
          </Box>
          <Box sx={BOX_STYLE}>
            <SelectInput
              key="timezone"
              source="timezone"
              choices={STATISTICS_TIMEZONES_CHOICES}
              validate={[required()]}
              {...FILTER_BASE_PROPS}
            />
          </Box>
          <Box sx={BOX_STYLE}>
            <SelectInput
              key="rebills_by"
              source="rebills_by"
              choices={STATISTICS_REBILLS_BY_CHOICES}
              validate={[required()]}
              {...FILTER_BASE_PROPS}
            />
          </Box>
          <Box sx={BOX_STYLE}>
            <BooleanInput
              key="is_sandbox"
              source="is_sandbox"
              {...FILTER_BASE_PROPS}
            />
          </Box>
        </Box>
        <Button sx={{ mb: 2 }} variant="outlined" color="primary" type="submit">
          Загрузить
        </Button>
      </form>
    </FormProvider>
  );
};
